import { put, putResolve, takeLatest } from "redux-saga/effects";

import {
  setApiCallError,
  setApiCallInProgress,
  setApiCallSuccess,
} from "@/actions/apiCallStatus.action";
import { showToast } from "@/actions/appStatus.action";
import { updateAPIProgress } from "@/actions/progress.action";
import {
  addQuestionAPI,
  getAllQuestionsAPI,
  removeQuestionAPI,
  updateQuestionAPI,
} from "@/api/questions.api";
import {
  ADD_QUESTION,
  GET_ALL_QUESTIONS,
  HIDE_MODAL,
  REMOVE_QUESTION,
  REMOVE_SOLUTION,
  UPDATE_QUESTION,
} from "@/constants/ActionTypes";
import { GET_ALL_QUESTION_API } from "@/constants/ApiNameConstants";
import { HTTP_STATUS_CODES } from "@/constants/HttpConstants";
import {
  GET_QUESTIONS_API_HTTP_STATUS_CODES,
  GET_QUESTIONS_ERROR_MESSAGE,
  ADD_QUESTION_API_HTTP_STATUS_CODES,
  ADD_QUESTION_ERROR_MESSAGE,
  UPDATE_QUESTION_API_HTTP_STATUS_CODES,
  UPDATE_QUESTION_ERROR_MESSAGE,
  REMOVE_QUESTION_API_HTTP_STATUS_CODES,
  REMOVE_QUESTION_ERROR_MESSAGE,
} from "@/constants/Questions";
import { IInitialState } from "@/types/initialState.types";
import { EToastType } from "@/components/common/AlertToast/AlertToast.types";
import {
  INTERVIEW_ADD_QUESTION,
  INTERVIEW_REMOVE_QUESTION,
  INTERVIEW_UPDATE_QUESTION,
} from "@/constants/SocketEventConstants";
import apiErrorHandler from "@/utils/apiErrorHandler";
import {
  EQuestionSource,
  IAddQuestionArgs,
  IAddQuestionPayload,
  IGetAllQuestionsAction,
  IRemoveQuestionArgs,
  IUpdateQuestionArgs,
  TUpdateQuestionPayload,
} from "../types/questions.types";
import { encodeData } from "../utils/encodeDecode.utils";
import {
  goToAddQuestionRoute,
  goToCandidateWaitRoute,
  goToQuestionRoute,
} from "../utils/switchRoute.utils";
import { call, select } from "./utils.saga";

function* getAllQuestions({ payload }: IGetAllQuestionsAction) {
  const { interviewSlug, navigate } = payload;
  try {
    yield put(setApiCallInProgress(GET_ALL_QUESTION_API));
    const isCandidate = yield* select(
      (state: IInitialState) => state.auth.userIdentityData?.isCandidate,
    );
    const getAllQuestionsResp = yield* call(getAllQuestionsAPI, interviewSlug);
    if (
      getAllQuestionsResp.status === HTTP_STATUS_CODES.OK &&
      Object.values(getAllQuestionsResp.data).length
    ) {
      yield putResolve({
        type: `${GET_ALL_QUESTIONS}_SUCCESS`,
        payload: {
          questions: getAllQuestionsResp.data,
        },
      });
    }
    const firstQuestionSlug =
      getAllQuestionsResp.data && Object.keys(getAllQuestionsResp.data)[0];
    if (interviewSlug && isCandidate && navigate) {
      if (firstQuestionSlug) {
        goToQuestionRoute(navigate, interviewSlug, firstQuestionSlug, {
          replace: true,
        });
      } else {
        goToCandidateWaitRoute(navigate, interviewSlug, { replace: true });
      }
    }
    yield put(setApiCallSuccess(GET_ALL_QUESTION_API));
  } catch (error) {
    yield put(setApiCallError(GET_ALL_QUESTIONS));
    const message = yield* call(
      apiErrorHandler,
      error,
      GET_QUESTIONS_API_HTTP_STATUS_CODES,
      GET_QUESTIONS_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* addQuestion({ payload }: IAddQuestionArgs) {
  try {
    const {
      name,
      description,
      questionType,
      source,
      interviewSlug,
      problemSlug,
      navigate,
    } = payload;

    const questions = yield* select(
      (state: IInitialState) => state.questionsDetails.questions,
    );

    const tabIndex = Object.keys(questions).length + 1 || 1;

    if (
      name &&
      questionType &&
      source &&
      interviewSlug &&
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      ((source === EQuestionSource.IMPORTED && problemSlug) ||
        source === EQuestionSource.CREATED)
    ) {
      const requestPayload: IAddQuestionPayload = {
        description,
        name,
        question_type: questionType,
        source,
        tab_index: tabIndex,
        problem_slug: problemSlug,
      };

      yield put(updateAPIProgress(true));

      const addQuestionResp = yield* call(
        addQuestionAPI,
        interviewSlug,
        requestPayload,
      );
      if (addQuestionResp.status === HTTP_STATUS_CODES.CREATED) {
        yield putResolve({
          type: `${ADD_QUESTION}_SUCCESS`,
          payload: {
            question: addQuestionResp.data,
          },
        });
        const socket = yield* select(
          (state: IInitialState) => state.socket.socket,
        );
        socket?.emit(
          INTERVIEW_ADD_QUESTION,
          encodeData({
            questionSlug: addQuestionResp.data.slug,
            question: addQuestionResp.data,
          }),
        );
        goToQuestionRoute(navigate, interviewSlug, addQuestionResp.data.slug);
      }
    }
  } catch (error: unknown) {
    const message = yield* call(
      apiErrorHandler,
      error,
      ADD_QUESTION_API_HTTP_STATUS_CODES,
      ADD_QUESTION_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  } finally {
    yield put(updateAPIProgress(false));
  }
}
function* updateQuestion({ payload }: IUpdateQuestionArgs) {
  try {
    const {
      name,
      description,
      questionSlug,
      interviewSlug,
      source,
      problemSlug,
      questionType,
      navigate,
    } = payload;
    let requestPayload: TUpdateQuestionPayload = {
      description,
      name,
    };

    if (
      source &&
      source === EQuestionSource.IMPORTED &&
      problemSlug &&
      questionType
    ) {
      requestPayload = {
        name,
        problem_slug: problemSlug,
        question_type: questionType,
        source,
      };
    }
    yield put(updateAPIProgress(true));
    const updateQuestionResp = yield* call(
      updateQuestionAPI,
      interviewSlug,
      questionSlug,
      requestPayload,
    );
    if (updateQuestionResp.status === HTTP_STATUS_CODES.OK) {
      if (
        source &&
        source === EQuestionSource.IMPORTED &&
        problemSlug &&
        questionType
      ) {
        yield putResolve({
          type: REMOVE_SOLUTION,
          payload: {
            questionSlug,
          },
        });
      }
      yield putResolve({
        type: `${UPDATE_QUESTION}_SUCCESS`,
        payload: {
          question: updateQuestionResp.data,
        },
      });
      const socket = yield* select(
        (state: IInitialState) => state.socket.socket,
      );
      socket?.emit(
        INTERVIEW_UPDATE_QUESTION,
        encodeData({
          questionSlug: updateQuestionResp.data.slug,
          question: updateQuestionResp.data,
        }),
      );
      if (navigate) goToQuestionRoute(navigate, interviewSlug, questionSlug);
    }
  } catch (error: unknown) {
    const message = yield* call(
      apiErrorHandler,
      error,
      UPDATE_QUESTION_API_HTTP_STATUS_CODES,
      UPDATE_QUESTION_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  } finally {
    yield put(updateAPIProgress(false));
  }
}

function* removeQuestion({ payload }: IRemoveQuestionArgs) {
  try {
    const {
      interviewSlug,
      questionSlug,
      navigate,
      redirectQuestionSlug,
      redirectToLandingPage,
      isCurrentQuestionRemove,
    } = payload;
    const removeQuestionResp = yield* call(
      removeQuestionAPI,
      interviewSlug,
      questionSlug,
    );
    if (removeQuestionResp.status === HTTP_STATUS_CODES.OK) {
      yield putResolve({
        type: `${REMOVE_QUESTION}_SUCCESS`,
        payload: {
          questionSlug,
        },
      });
      yield put({
        type: HIDE_MODAL,
      });
      const socket = yield* select(
        (state: IInitialState) => state.socket.socket,
      );
      socket?.emit(
        INTERVIEW_REMOVE_QUESTION,
        encodeData({
          questionSlug,
          redirectQuestionSlug,
          redirectToLandingPage,
        }),
      );
      if (redirectQuestionSlug && isCurrentQuestionRemove) {
        goToQuestionRoute(navigate, interviewSlug, redirectQuestionSlug);
      } else if (redirectToLandingPage && !redirectQuestionSlug) {
        goToAddQuestionRoute(navigate, interviewSlug);
      }
    }
  } catch (error) {
    const message = yield* call(
      apiErrorHandler,
      error,
      REMOVE_QUESTION_API_HTTP_STATUS_CODES,
      REMOVE_QUESTION_ERROR_MESSAGE,
    );
    if (message) yield put(showToast({ message, type: EToastType.ERROR }));
  }
}

export default function* loadQuestionsSaga() {
  yield takeLatest(GET_ALL_QUESTIONS, getAllQuestions);
  yield takeLatest(ADD_QUESTION, addQuestion);
  yield takeLatest(UPDATE_QUESTION, updateQuestion);
  yield takeLatest(REMOVE_QUESTION, removeQuestion);
}
